class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.selectedValues = new Map();
  }

  connectedCallback() {
    this.initializeSelectedValues();
    this.setupDropdowns();
  }

  initializeSelectedValues() {
    // Get the currently selected variant to initialize all option values
    const selectedVariantScript = this.querySelector('[data-selected-variant]');
    if (selectedVariantScript) {
      try {
        const selectedVariant = JSON.parse(selectedVariantScript.textContent);
        if (selectedVariant && selectedVariant.options) {
          selectedVariant.options.forEach((value, index) => {
            this.selectedValues.set(index, value);
          });
        }
      } catch (error) {
        console.error('Error parsing selected variant:', error);
      }
    }
  }

  setupDropdowns() {
    const dropdowns = this.querySelectorAll('[data-variant-dropdown]');
    
    dropdowns.forEach((dropdown) => {
      const button = dropdown.querySelector('.variant-dropdown__select');
      const panel = dropdown.querySelector('.disclosure__list-wrapper');
      const optionButtons = dropdown.querySelectorAll('[data-variant-option]');
      const selectedValueSpan = dropdown.querySelector('[data-selected-value]');
      const chevron = dropdown.querySelector('[data-chevron]');
      
      if (!button || !panel) return;

      // Get option position
      const optionPosition = parseInt(button.dataset.optionPosition);

      // Hide panel function
      const hidePanel = () => {
        button.setAttribute('aria-expanded', 'false');
        panel.setAttribute('hidden', true);
        if (chevron) {
          chevron.style.transform = 'rotate(90deg)';
        }
        document.removeEventListener('click', closeOnClickOutside);
      };

      // Open selector function
      const openSelector = () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.focus();
        panel.toggleAttribute('hidden');
        button.setAttribute(
          'aria-expanded',
          (!isOpen).toString()
        );
        
        // Rotate chevron
        if (chevron) {
          chevron.style.transform = !isOpen ? 'rotate(270deg)' : 'rotate(90deg)';
        }
        
        // Check available space and position dropdown
        if (!isOpen) {
          const buttonRect = button.getBoundingClientRect();
          const panelHeight = panel.offsetHeight;
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;
          
          // Open upward if not enough space below and more space above
          if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
            panel.style.bottom = '0%';
            panel.style.top = 'auto';
            panel.style.transform = 'translateY(-1px)';
            panel.style.paddingTop = '0rem';
            panel.style.paddingBottom = '2.25rem';

          } else {
            panel.style.bottom = 'auto';
            panel.style.top = '0%';
            panel.style.transform = 'translateY(2px)';
            panel.style.paddingTop = '2.25rem';
            panel.style.paddingBottom = '0';
          }
        }
        
        // Add click outside listener when opening
        if (!isOpen) {
          // Use setTimeout to prevent the current click from immediately closing it
          setTimeout(() => {
            document.addEventListener('click', closeOnClickOutside);
          }, 0);
        } else {
          document.removeEventListener('click', closeOnClickOutside);
        }
      };

      // Close selector on focus out
      const closeSelector = (event) => {
        if (!dropdown.contains(event.target) && !dropdown.contains(event.relatedTarget)) {
          hidePanel();
        }
      };

      // Close selector on click outside
      const closeOnClickOutside = (event) => {
        if (!dropdown.contains(event.target) && button.getAttribute('aria-expanded') === 'true') {
          hidePanel();
        }
      };

      // Keyboard navigation  
      const onKeyDown = (event) => {
        // Only handle arrow keys for navigation within the dropdown
        // Don't interfere with Space/Enter on the button
        if (event.code.toUpperCase() !== 'ARROWUP' && event.code.toUpperCase() !== 'ARROWDOWN') {
          return;
        }
        
        const focusableItems = Array.from(dropdown.querySelectorAll('[data-variant-option]:not([disabled])'));
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }
      };

      const onKeyUp = (event) => {
        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (button.getAttribute('aria-expanded') == 'false') return;
            event.preventDefault();
            hidePanel();
            event.stopPropagation();
            button.focus();
            break;
        }
      };

      const onButtonKeyDown = (event) => {
        switch (event.code.toUpperCase()) {
          case 'SPACE':
          case 'ENTER':
            event.preventDefault();
            if (button.getAttribute('aria-expanded') == 'true') {
              hidePanel();
            } else {
              openSelector();
            }
            break;
        }
      };

      // Button click to toggle
      button.addEventListener('click', (e) => {
        e.preventDefault();
        openSelector();
      });

      button.addEventListener('keydown', onButtonKeyDown);

      // Option button clicks
      optionButtons.forEach((optionButton) => {
        optionButton.addEventListener('click', (e) => {
          e.preventDefault();
          
          if (optionButton.disabled) return;

          const value = optionButton.dataset.value;
          const optionValueId = optionButton.dataset.optionValueId;

          // Update selected value in map
          this.selectedValues.set(optionPosition - 1, value);

          // Update UI
          selectedValueSpan.textContent = value.replace('-', '–');
          
          // Update aria-current
          optionButtons.forEach(btn => btn.removeAttribute('aria-current'));
          optionButton.setAttribute('aria-current', 'true');
          
          // Update background classes
          optionButtons.forEach(btn => {
            btn.classList.remove('bg-highlight');
            btn.classList.add('bg-white');
          });
          optionButton.classList.remove('bg-white');
          optionButton.classList.add('bg-highlight');

          // Close dropdown
          hidePanel();

          // Trigger variant update BEFORE focusing (important!)
          this.onVariantChange(e, optionButton);

          // Return focus to the button for better keyboard navigation (delayed to ensure variant update completes)
          setTimeout(() => {
            button.focus();
          }, 10);
        });
      });

      // Event listeners
      dropdown.addEventListener('keyup', onKeyUp);
      dropdown.addEventListener('keydown', onKeyDown);
      dropdown.addEventListener('focusout', closeSelector);

      // Initialize button state
      button.setAttribute('aria-expanded', 'false');
    });
  }

  onVariantChange(event, target) {
    // Handle the change and update variant
    this.updateVariantInput();

    // Publish the event for product-info.js if the publish function exists
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const selectedOptionValues = this.getSelectedOptionValues();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: selectedOptionValues,
        },
      });
    }
  }

  updateVariantInput() {
    const selectedOptions = this.getSelectedOptions();
    const variants = this.getVariantData();
    const selectedVariant = this.findMatchingVariant(variants, selectedOptions);

    if (selectedVariant) {
      this.updateHiddenVariantInput(selectedVariant.id);
    }
  }

  getSelectedOptions() {
    // Get selected values from the map in order
    const options = [];
    // Find the maximum key to ensure we get all options
    const maxKey = Math.max(...Array.from(this.selectedValues.keys()));
    for (let i = 0; i <= maxKey; i++) {
      if (this.selectedValues.has(i)) {
        options.push(this.selectedValues.get(i));
      }
    }
    return options;
  }

  getSelectedOptionValues() {
    // Get option value IDs for pub/sub events
    const selectedButtons = this.querySelectorAll('[data-variant-option][aria-current="true"]');
    return Array.from(selectedButtons).map((button) => button.dataset.optionValueId);
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      return selectedOptions.every((selectedOption, index) => {
        return variant.options[index] === selectedOption;
      });
    });
  }

  updateHiddenVariantInput(variantId) {
    const variantInput = document.querySelector('.product-variant-id');
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  getVariantData() {
    const variantScript = this.querySelector('[id^="ProductJSON-"]');
    if (variantScript) {
      try {
        return JSON.parse(variantScript.textContent);
      } catch (error) {
        console.error('Error parsing variant data:', error);
        return [];
      }
    }
    return [];
  }
}

customElements.define('variant-selects', VariantSelects);
